import { getProductCatalogEntry } from '@/constants/product-catalog.ts';
import type { OrderLineItemInputDto } from '@/types/dtos/orders/request/order-line-item.dto';
import type { OrderLineItemDto } from '@/types/dtos/orders/response/order-line-item.dto';
import type { ProductDto } from '@/types/dtos/orders/response/product.dto';

function idFromRefValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const t = value.trim();
    return t || undefined;
  }
  if (
    value != null &&
    typeof value === 'object' &&
    typeof (value as { toString?: () => string }).toString === 'function'
  ) {
    const s = (value as { toString: () => string }).toString();
    if (typeof s !== 'string') return undefined;
    const t = s.trim();
    if (!t || t === '[object Object]') return undefined;
    return t;
  }
  return undefined;
}

/**
 * Resolves catalog product id for an order line's `product` (ObjectId string or populated product).
 * Returns undefined when neither `id` nor `_id` yields a usable string — do not send that to the API.
 */
export function getOrderLineItemProductId(product: OrderLineItemDto['product']): string | undefined {
  if (typeof product === 'string') {
    return idFromRefValue(product);
  }
  if (!product || typeof product !== 'object') {
    return undefined;
  }
  const rec = product as Record<string, unknown>;
  if (typeof rec.id === 'string' && rec.id.trim() !== '') {
    return rec.id.trim();
  }
  return idFromRefValue(rec._id);
}

/** Populated order line `product` exposes `key`; string refs do not. */
export function getOrderLineItemProductKey(product: OrderLineItemDto['product']): string | undefined {
  if (!product || typeof product === 'string') {
    return undefined;
  }
  const key = (product as { key?: unknown }).key;
  return typeof key === 'string' && key.trim() !== '' ? key : undefined;
}

export type OrderLineItemUpdatePayload = {
  productId: string;
  quantity: number;
  autoAssignToOrderScope?: boolean;
  contributorId?: string;
  dayId?: string;
};

export function orderLineItemScopeKey(item: { productId: string; contributorId?: string; dayId?: string }): string {
  return [item.productId, item.contributorId ?? '', item.dayId ?? ''].join(':');
}

function lineItemShouldAutoAssignToOrderScope(lineItem: OrderLineItemDto): boolean {
  if (lineItem.autoAssignToOrderScope) {
    return true;
  }
  const key = getOrderLineItemProductKey(lineItem.product);
  return Boolean(getProductCatalogEntry(key)?.autoAssignToOrderScope);
}

/**
 * Ensures PATCH `lineItems` include `autoAssignToOrderScope: true` for every SKU that defines it in the event
 * product catalog, using `products` (store/API list) to resolve `productId` → catalog key. Call from the
 * update-order mutation so all callers (checkout, alerts, contributor flows) behave consistently.
 */
export function enrichPatchOrderLineItemsWithCatalogAutoAssign(
  lineItems: OrderLineItemInputDto[],
  products: ProductDto[] | undefined,
): OrderLineItemInputDto[] {
  if (!lineItems.length) {
    return lineItems;
  }
  const byId = new Map((products ?? []).map((p) => [p.id, p] as const));
  return lineItems.map((li) => {
    if (li.autoAssignToOrderScope) {
      return li;
    }
    const p = byId.get(li.productId);
    const key = p?.key != null ? String(p.key) : undefined;
    if (getProductCatalogEntry(key)?.autoAssignToOrderScope) {
      return { ...li, autoAssignToOrderScope: true };
    }
    return li;
  });
}

/**
 * Maps API line items to update-order payloads. Optionally replaces quantity for one product id (single bump pass).
 * Preserves `autoAssignToOrderScope` from the line item and applies the same flag for any SKU marked in the
 * event product catalog (`EVENT_CATALOG_ENTRIES`), so the field is not dropped when callers omit it.
 * @throws If any line item has no resolvable product id
 */
export function mapOrderLineItemsToUpdatePayload(
  lineItems: OrderLineItemDto[],
  options?: { bumpProductId?: string; bumpQuantity?: number },
): OrderLineItemUpdatePayload[] {
  return lineItems.map((lineItem) => {
    const productId = getOrderLineItemProductId(lineItem.product);
    if (!productId) {
      throw new Error('Order line item is missing a resolvable product id');
    }
    const bump =
      options?.bumpProductId !== undefined && options.bumpQuantity !== undefined && productId === options.bumpProductId;
    const useAuto = lineItemShouldAutoAssignToOrderScope(lineItem);
    return {
      productId,
      quantity: bump ? options.bumpQuantity! : lineItem.quantity,
      ...(useAuto ? { autoAssignToOrderScope: true } : {}),
      ...(lineItem.contributorId ? { contributorId: lineItem.contributorId } : {}),
      ...(lineItem.dayId ? { dayId: lineItem.dayId } : {}),
    };
  });
}

/**
 * Adds or updates a scoped order line (e.g. remote speaker day per contributor) without creating
 * duplicate product + contributor + day keys on PATCH.
 */
export function upsertOrderLineItemInPayload(
  lineItems: OrderLineItemDto[],
  newLine: OrderLineItemInputDto,
): OrderLineItemUpdatePayload[] {
  const mapped = mapOrderLineItemsToUpdatePayload(lineItems);
  const newKey = orderLineItemScopeKey(newLine);
  const existingIndex = mapped.findIndex((item) => orderLineItemScopeKey(item) === newKey);
  if (existingIndex >= 0) {
    return mapped.map((item, index) =>
      index === existingIndex ? { ...item, quantity: Math.max(item.quantity, newLine.quantity) } : item,
    );
  }
  return [...mapped, newLine];
}
